import {
    Contact,
    General,
    UploadedCertificates,
    UploadedInstallations,
} from '@authorizedInstaller/components/display/dialogs';
import { AUTHORIZED_INSTALLER_TABS, useAuthorizedInstallersDashboardContext } from '@authorizedInstaller/contexts';
import {
    useAcceptAuthorizedInstallerRequestMutation,
    useRejectAuthorizedInstallerRequestMutation,
} from '@authorizedInstaller/services/authorizedInstallerProgram';
import { CloseButton, HoldButton } from '@components/utility/buttons';
import { useAppDispatch } from '@hooks/generic';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import CheckIcon from '@mui/icons-material/Check';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CloseIcon from '@mui/icons-material/Close';
import { Card, Chip, CircularProgress, Dialog, DialogProps, Tab, Tabs, Typography } from '@mui/material';
import { api } from '@services/api';
import { handleError, toaster } from '@utils/common';
import clsx from 'clsx';
import { useCallback, useMemo, useState } from 'react';

enum TAB_TITLES {
    GENERAL = 'General',
    CONTACT = 'Contact',
    UPLOADED_CERTIFICATES = 'Uploaded Certificates',
    UPLOADED_INSTALLATIONS = 'Uploaded Installations',
    //ONSITE_EVENT_PARTICIPATION = 'Onsite Event Participation',
}

enum TABS {
    GENERAL = 0,
    CONTACT = 1,
    UPLOADED_CERTIFICATES = 2,
    UPLOADED_INSTALLATIONS = 3,
    //ONSITE_EVENT_PARTICIPATION = 4,
}

const AuthorizedInstallerDialog = (props: DialogProps) => {
    const { tab: outsideTab, installerRequest } = useAuthorizedInstallersDashboardContext();
    const [acceptRequest] = useAcceptAuthorizedInstallerRequestMutation();
    const [rejectRequest] = useRejectAuthorizedInstallerRequestMutation();
    const dispatch = useAppDispatch();

    const [tab, setTab] = useState(0);
    const [state, setState] = useState<'loading' | 'declined' | 'accepted' | null>(null);
    const handleClose = useCallback(() => {
        if (state !== 'loading') {
            props.onClose?.({}, 'escapeKeyDown');
            setTab(0);
            setState(null);
        }
    }, [props, state]);

    const TabContent = useMemo(() => {
        switch (tab) {
            case TABS.UPLOADED_CERTIFICATES:
                return <UploadedCertificates />;
            case TABS.UPLOADED_INSTALLATIONS:
                return <UploadedInstallations />;
            case TABS.CONTACT:
                return <Contact />;
            /* case TABS.ONSITE_EVENT_PARTICIPATION:
                return <OnsiteEventParticipation />; */
            case TABS.GENERAL:
            default:
                return <General />;
        }
    }, [tab]);

    const handleAccept = async () => {
        try {
            setState('loading');
            if (!installerRequest?.id) {
                throw 'No ID associated';
            }
            await acceptRequest(installerRequest.id).unwrap();
            setState('accepted');
            toaster.success({ msg: 'Application Form Was Accepted', title: 'ACCEPTED' });
            dispatch(api.util.invalidateTags(['AuthorizedInstallerRequests']));
        } catch (err) {
            handleError('Error accepting request', err);
            setState(null);
        }
    };

    const handleDecline = async () => {
        try {
            setState('loading');
            if (!installerRequest?.id) {
                throw 'No ID associated';
            }
            await rejectRequest(installerRequest.id).unwrap();
            setState('declined');
            toaster.failure({ msg: 'Application Form Was Declined', title: 'DECLINED' });
            dispatch(api.util.invalidateTags(['AuthorizedInstallerRequests']));
        } catch (err) {
            handleError('Error accepting request', err);
            setState(null);
        }
    };

    const statusAccepted = useMemo(() => installerRequest?.status === 'accepted', [installerRequest?.status]);

    return (
        <Dialog
            {...props}
            onClose={handleClose}
            slotProps={{
                paper: { className: 'min-w-0 min-w-[1150px] px-9 py-10 relative' },
            }}
        >
            <div className={clsx({ 'blur-sm pointer-events-none': state === 'loading' })}>
                <CloseButton onClick={handleClose} className="inline-flex" />
                <Tabs
                    value={tab}
                    onChange={(_e, newTab) => setTab(newTab)}
                    className="border-b "
                    slotProps={{
                        indicator: {
                            sx: {
                                display: 'block',
                            },
                        },
                    }}
                >
                    <Tab label={<Typography color="black.main">{TAB_TITLES.GENERAL}</Typography>} />
                    <Tab label={<Typography color="black.main">{TAB_TITLES.CONTACT}</Typography>} />
                    <Tab
                        label={
                            <Typography color="black.main">
                                {TAB_TITLES.UPLOADED_CERTIFICATES}{' '}
                                <Typography component="span" className="ml-2 opacity-40">
                                    {installerRequest?.certificate_files.length} uploaded
                                </Typography>
                            </Typography>
                        }
                    />
                    <Tab
                        label={
                            <Typography color="black.main">
                                {TAB_TITLES.UPLOADED_INSTALLATIONS}{' '}
                                <Typography component="span" className="ml-2 opacity-40">
                                    {installerRequest?.installation_files.length} uploaded
                                </Typography>
                            </Typography>
                        }
                    />
                    {/* <Tab label={<Typography color="black.main">{TAB_TITLES.ONSITE_EVENT_PARTICIPATION}</Typography>} /> */}
                </Tabs>
                <div className="py-9">
                    <Typography fontWeight={700} fontSize={20}>
                        {Object.values(TAB_TITLES)[tab]}
                    </Typography>
                    <div className="my-10 h-[330px]">{TabContent}</div>
                    {outsideTab === AUTHORIZED_INSTALLER_TABS.NEW_FORMS ? (
                        <div className="flex mt-2 w-[450px] h-[40px] ml-auto justify-center relative">
                            {!['accepted', 'declined'].includes(state as string) ? (
                                <>
                                    <div
                                        className={clsx('flex gap-2', {
                                            'blur-sm pointer-events-none': state === 'loading',
                                        })}
                                    >
                                        <HoldButton
                                            holdTime={1000}
                                            onHoldComplete={handleDecline}
                                            color="error"
                                            className="w-[220px] h-full"
                                            holdBackgrondPalleteKey="error-300"
                                            startIcon={<CloseIcon className="z-10" />}
                                            iconOnHold
                                            disableElevation={false}
                                            holdText="HOLD TO DECLINE"
                                        >
                                            <Typography
                                                fontFamily="Roboto"
                                                fontWeight={500}
                                                variant="body1"
                                                lineHeight="22px"
                                            >
                                                DECLINE
                                            </Typography>
                                        </HoldButton>
                                        <HoldButton
                                            holdTime={1000}
                                            onHoldComplete={handleAccept}
                                            className="w-[220px] h-full"
                                            startIcon={<CheckIcon className="z-10" />}
                                            iconOnHold
                                            disableElevation={false}
                                            holdText="HOLD TO ACCEPT"
                                        >
                                            <Typography
                                                fontFamily="Roboto"
                                                fontWeight={500}
                                                variant="body1"
                                                lineHeight="22px"
                                            >
                                                ACCEPT
                                            </Typography>
                                        </HoldButton>
                                    </div>
                                </>
                            ) : (
                                <Card
                                    className={clsx('w-full mx-3 flex items-center justify-center gap-1', {
                                        'bg-confirm-500': state === 'accepted',
                                        'bg-error-500': state === 'declined',
                                    })}
                                >
                                    {state === 'accepted' ? (
                                        <CheckCircleRoundedIcon color="white" />
                                    ) : (
                                        <CancelRoundedIcon color="white" />
                                    )}
                                    <Typography color="white.main" fontWeight={500} fontFamily="Roboto">
                                        {state?.toUpperCase()}
                                    </Typography>
                                </Card>
                            )}
                        </div>
                    ) : (
                        <Chip
                            color={statusAccepted ? 'light-confirm' : 'light-error'}
                            className="w-[205px] rounded h-[50px]"
                            classes={{
                                label: 'font-semibold text-base',
                            }}
                            label={statusAccepted ? 'FORM ACCEPTED' : 'FORM DECLINED'}
                            icon={
                                statusAccepted ? (
                                    <CheckCircleOutlineIcon color="check-confirm" />
                                ) : (
                                    <CancelOutlinedIcon color="error" />
                                )
                            }
                        />
                    )}
                </div>
            </div>
            {state === 'loading' ? <CircularProgress className="absolute top-2/4 right-2/4" /> : null}
        </Dialog>
    );
};

export { AuthorizedInstallerDialog };
